// DonorOnboarding — v2 port of v1's web/dashboard/src/pages/
// DonorOnboarding.tsx. CHG-1519 / ITER-3638 (donor-portal wave 2).
//
// v2 minimal port: single welcome screen + "Get started" button that
// POSTs /my-giving/complete-onboarding. v1's 5-step wizard (CHG-0793 /
// CHG-0887 FormDialog page-wizard with annual-report opt-in, MFA setup
// prompt, SSO link prompts, etc.) is a follow-up — the v2 minimum is
// "let the donor mark onboarding complete and proceed to /my-giving".
//
// Mounts at /my-giving/onboarding (v2-canonical URL scheme; backend
// endpoints stay at /api/v1/my-giving/*). If the user is ALREADY
// onboarded (status.completed === true), the page bounces straight
// to /my-giving. Wraps in ProtectedRoute auth-only (donor owns their own
// onboarding).

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormatters } from "../contexts/IntlContext";
import {
  fetchOnboardingStatus,
  completeOnboarding,
  type OnboardingStatus,
} from "../api/my-giving";

const QUERY_KEY = ["me", "onboarding-status"] as const;

export function DonorOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatDateTime } = useFormatters();

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchOnboardingStatus,
  });

  // Already-onboarded users skip the welcome and go straight to /my-giving.
  useEffect(() => {
    if (data?.completed) {
      navigate("/my-giving", { replace: true });
    }
  }, [data?.completed, navigate]);

  const mutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      navigate("/my-giving", { replace: true });
    },
  });

  return (
    <main
      data-testid="donor-onboarding"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--color-bg-subtle, #f6f7fb)",
      }}
    >
      <section
        style={{
          maxWidth: 560,
          width: "100%",
          padding: "40px 32px",
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-medium, 0 12px 30px rgba(0,0,0,0.08))",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 16,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          Welcome to your donor portal
        </h1>
        <p
          style={{
            margin: 0,
            color: "var(--color-text-secondary)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          A single place for your giving history, recurring gifts, pledges,
          receipts, profile, and preferences — across every organization you
          support. We&rsquo;ll keep your details in sync so each org only sees
          what you tell them.
        </p>

        {isLoading && (
          <p
            data-testid="onboarding-loading"
            style={{ fontSize: 13, color: "var(--color-text-muted)" }}
          >
            Loading your onboarding status…
          </p>
        )}

        {error && !isLoading && (
          <div
            data-testid="onboarding-fetch-error"
            role="alert"
            style={{
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--color-danger, #b91c1c)",
              background: "var(--color-danger-bg, #fef2f2)",
              borderRadius: "var(--radius-sm, 6px)",
            }}
          >
            Couldn&rsquo;t load your onboarding status. Try the button below
            anyway — if your account is already set up, you&rsquo;ll land at
            the donor portal home.
          </div>
        )}

        {/* When loaded + still incomplete, surface the start button.
            already-completed accounts are redirected via useEffect above. */}
        {!isLoading && data && !data.completed && (
          <p
            data-testid="onboarding-pending"
            style={{ fontSize: 13, color: "var(--color-text-muted)" }}
          >
            We&rsquo;ll mark your account as onboarded so you don&rsquo;t see
            this screen again.
          </p>
        )}

        {!isLoading && data?.completed && (
          <p
            data-testid="onboarding-completed-pre-redirect"
            style={{ fontSize: 13, color: "var(--color-text-muted)" }}
          >
            You&rsquo;re already onboarded — taking you to your donor home…
            {data.onboarding_completed_at && (
              <>
                {" "}
                (completed {formatDateTime(data.onboarding_completed_at)})
              </>
            )}
          </p>
        )}

        {mutation.error && (
          <div
            data-testid="onboarding-complete-error"
            role="alert"
            style={{
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--color-danger, #b91c1c)",
              background: "var(--color-danger-bg, #fef2f2)",
              borderRadius: "var(--radius-sm, 6px)",
            }}
          >
            Couldn&rsquo;t mark onboarding complete. Try again, or sign in to
            app.aloragiving.com which has the full v1 onboarding wizard.
          </div>
        )}

        <button
          type="button"
          data-action="complete-onboarding"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || data?.completed}
          style={{
            marginTop: 8,
            padding: "10px 16px",
            fontSize: 14,
            fontWeight: 500,
            border: "none",
            borderRadius: "var(--radius-md, 8px)",
            background: "var(--color-primary)",
            color: "var(--color-on-primary, #fff)",
            cursor:
              mutation.isPending || data?.completed ? "not-allowed" : "pointer",
            opacity: mutation.isPending || data?.completed ? 0.6 : 1,
          }}
        >
          {mutation.isPending ? "Finishing…" : "Get started"}
        </button>
      </section>
    </main>
  );
}

export type { OnboardingStatus };
